import {useCallback, useEffect, useRef, useState} from "react";
import {FabricObject} from "fabric";

interface UseEditableTextFieldProps {
    element: FabricObject;
    initialValue: string;
    onSave: (value: string) => void;
    onUnmountSave: (value: string, elementId: string | null) => void;
}

export const useEditableTextField = ({
                                         element,
                                         initialValue,
                                         onSave,
                                         onUnmountSave,
                                     }: UseEditableTextFieldProps) => {
    const [localValue, setLocalValue] = useState(initialValue);
    const pendingRef = useRef<{
        value: string;
        elementId: string | null
    } | null>(null);
    const initialValueRef = useRef(initialValue);

    useEffect(() => {
        setLocalValue(initialValue);
        initialValueRef.current = initialValue;
        pendingRef.current = null;
    }, [element, initialValue]);

    const handleChange = useCallback((value: string) => {
        setLocalValue(value);
        pendingRef.current = {value, elementId: element.floorPlanItem?.id ?? null};
    }, [element]);

    const handleBlur = useCallback(() => {
        const pending = pendingRef.current;
        if (!pending) return;

        const trimmed = pending.value.trim();
        if (pending.value !== initialValueRef.current) {
            onSave(trimmed);
        }
        pendingRef.current = null;
    }, [onSave]);

    useEffect(() => {
        return () => {
            const pending = pendingRef.current;
            if (!pending) return;

            const trimmed = pending.value.trim();
            if (pending.value !== initialValueRef.current) {
                onUnmountSave(trimmed, pending.elementId);
            }
        };
    }, [onUnmountSave]);

    return {
        value: localValue,
        onChange: handleChange,
        onBlur: handleBlur,
    };
};
